import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Instagram, Facebook, Linkedin, Youtube, Twitter } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const [settings, setSettings] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  const handleNewsletterSubscribe = async (e) => {
    e.preventDefault();
    
    if (!newsletterEmail) {
      alert('Por favor, digite seu email');
      return;
    }

    setSubscribing(true);

    try {
      // Aqui você pode implementar o endpoint de newsletter no backend
      // Por enquanto, apenas mostra uma mensagem
      await new Promise(resolve => setTimeout(resolve, 1000));
      alert('Obrigado por se inscrever! Em breve você receberá nossas novidades.');
      setNewsletterEmail('');
    } catch (error) {
      console.error('Erro ao inscrever na newsletter:', error);
      alert('Erro ao se inscrever. Tente novamente.');
    } finally {
      setSubscribing(false);
    }
  };

  // Se o footer estiver desabilitado ou configurações não carregaram, não renderizar
  if (!settings || !settings.footer_enabled) {
    return null;
  }

  return (
    <footer className="bg-gray-900 text-gray-300 mt-auto">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          
          {/* Logo e Redes Sociais */}
          {settings.footer_show_social && (
            <div>
              <h2 className="text-white text-2xl font-bold mb-4">
                {settings.siteName || 'ArqessenceShop'}
              </h2>
              {settings.footer_about && (
                <p className="text-sm mb-4 text-gray-400">
                  {settings.footer_about}
                </p>
              )}
              
              <div className="flex gap-3 mt-4">
                {settings.footer_instagram && (
                  <a 
                    href={settings.footer_instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-gray-800 hover:bg-[#00A86B] flex items-center justify-center transition-colors"
                    aria-label="Instagram"
                  >
                    <Instagram size={18} />
                  </a>
                )}
                {settings.footer_facebook && (
                  <a 
                    href={settings.footer_facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-gray-800 hover:bg-[#00A86B] flex items-center justify-center transition-colors"
                    aria-label="Facebook"
                  >
                    <Facebook size={18} />
                  </a>
                )}
                {settings.footer_linkedin && (
                  <a 
                    href={settings.footer_linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-gray-800 hover:bg-[#00A86B] flex items-center justify-center transition-colors"
                    aria-label="LinkedIn"
                  >
                    <Linkedin size={18} />
                  </a>
                )}
                {settings.footer_youtube && (
                  <a 
                    href={settings.footer_youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-gray-800 hover:bg-[#00A86B] flex items-center justify-center transition-colors"
                    aria-label="YouTube"
                  >
                    <Youtube size={18} />
                  </a>
                )}
                {settings.footer_twitter && (
                  <a 
                    href={settings.footer_twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-gray-800 hover:bg-[#00A86B] flex items-center justify-center transition-colors"
                    aria-label="Twitter"
                  >
                    <Twitter size={18} />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Contato */}
          {settings.footer_show_contact && (
            <div>
              <h3 className="text-white text-lg font-semibold mb-4">Contato</h3>
              <div className="space-y-3">
                {settings.footer_address && (
                  <div className="flex items-start gap-2">
                    <MapPin size={18} className="text-[#00A86B] mt-1 flex-shrink-0" />
                    <p className="text-sm">{settings.footer_address}</p>
                  </div>
                )}
                {settings.footer_phone && (
                  <div className="flex items-center gap-2">
                    <Phone size={18} className="text-[#00A86B] flex-shrink-0" />
                    <a href={`tel:${settings.footer_phone}`} className="text-sm hover:text-[#00A86B] transition-colors">
                      {settings.footer_phone}
                    </a>
                  </div>
                )}
                {settings.footer_email && (
                  <div className="flex items-center gap-2">
                    <Mail size={18} className="text-[#00A86B] flex-shrink-0" />
                    <a href={`mailto:${settings.footer_email}`} className="text-sm hover:text-[#00A86B] transition-colors">
                      {settings.footer_email}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Links Principais */}
          {settings.footer_show_links && (
            <div>
              <h3 className="text-white text-lg font-semibold mb-4">Principais</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-sm hover:text-[#00A86B] transition-colors">
                    Início
                  </Link>
                </li>
                <li>
                  <Link to="/" className="text-sm hover:text-[#00A86B] transition-colors">
                    Projetos
                  </Link>
                </li>
                <li>
                  <Link to="/minha-conta" className="text-sm hover:text-[#00A86B] transition-colors">
                    Minha Conta
                  </Link>
                </li>
                <li>
                  <Link to="/carrinho" className="text-sm hover:text-[#00A86B] transition-colors">
                    Carrinho
                  </Link>
                </li>
              </ul>
            </div>
          )}

          {/* Newsletter */}
          {settings.footer_show_newsletter && settings.footer_newsletter_enabled && (
            <div>
              <h3 className="text-white text-lg font-semibold mb-4">Newsletter</h3>
              <p className="text-sm text-gray-400 mb-4">
                Fique por dentro de todas as notícias e novidades aqui da ArqessenceShop!
              </p>
              <form onSubmit={handleNewsletterSubscribe} className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Seu melhor e-mail"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                  required
                />
                <Button
                  type="submit"
                  disabled={subscribing}
                  className="bg-[#00A86B] hover:bg-[#00A86B]/90 text-white px-6"
                >
                  {subscribing ? '...' : '→'}
                </Button>
              </form>
            </div>
          )}
        </div>

        {/* Linha separadora */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-wrap justify-center md:justify-start gap-4 text-sm">
              <Link to="/politica-privacidade" className="hover:text-[#00A86B] transition-colors">
                Políticas de privacidade
              </Link>
              <Link to="/politica-devolucao" className="hover:text-[#00A86B] transition-colors">
                Política de devolução
              </Link>
              <Link to="/termos-uso" className="hover:text-[#00A86B] transition-colors">
                Termos de uso
              </Link>
            </div>
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} {settings.siteName || 'ArqessenceShop'}. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
